<template>
  <div :class="ns.b()">
    <aside :class="ns.e('aside')" aria-label="消息中心">
      <div v-loading="loading" :class="ns.e('aside-body')">
        <MessageFilterBar
          v-model:keyword="keyword"
          @create-group="groupChatVisible = true"
        />
        <div :class="ns.e('list')">
          <z-empty
            v-if="!loading && !filteredConversations.length"
            description="没有匹配的会话"
          />
          <MessageListItem
            v-for="item in filteredConversations"
            :key="item.id"
            :conversation="item"
            :selected="item.id === selectedId"
            @select="handleSelect"
            @command="handleMenuCommand"
          />
        </div>
      </div>
    </aside>

    <MessageDetailPanel
      :conversation="selectedConversation"
      :messages="selectedThread"
      :watermark="watermark"
      @invite="groupChatVisible = true"
      @toggle-star="handleToggleStar"
      @mark-read="handleMarkRead"
      @mark-unread="handleMarkUnread"
      @hide="handleHide"
      @remove="handleRemove"
      @open="handleOpen"
      @send="handleSendText"
      @image="handleSendImage"
      @file="handleSendFile"
      @direct-chat="handleDirectChat"
    />

    <MessageGroupChatDialog
      v-model="groupChatVisible"
      :load-org="props.loadOrg"
      @confirm="handleCreateGroup"
    />

    <MessageLabelDialog
      v-model="labelDialogVisible"
      :labels="labelDraft"
      @confirm="handleSaveLabels"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import ZEmpty from '@zoryn/components/empty'
import { ZMessage } from '@zoryn/components/message'
import { ZMessageBox } from '@zoryn/components/message-box'
import MessageDetailPanel from './components/MessageDetailPanel.vue'
import MessageFilterBar from './components/MessageFilterBar.vue'
import MessageGroupChatDialog from './components/MessageGroupChatDialog.vue'
import MessageLabelDialog from './components/MessageLabelDialog.vue'
import MessageListItem from './components/MessageListItem.vue'
import { useMessageList } from './composables/use-message-list'
import { messageCenterEmits, messageCenterProps } from './message-center'
import { setMessageCenterUnread } from './unread'
import { conversationToRecord, mapConversationDto, mapMessageDto, recordsToInbox } from './utils/message-mapper'
import { createMessageApi, type ImRealtimeEvent } from './utils/message-api'
import {
  buildConversationFromMembers,
  nextMessageId,
} from './utils/conversation-utils'
import type { GroupChatMember } from './utils/group-chat'
import {
  mockChatConversations,
  mockChatThreads,
  mockMessages,
} from './utils/message-mock'
import { BIZ_TAB_LABELS, type Conversation, type ConversationMember, type ConversationMenuCommand } from './types'

defineOptions({
  name: 'ZMessageCenter',
})

const props = defineProps(messageCenterProps)
const emit = defineEmits(messageCenterEmits)

const ns = useNamespace('message-center')
const loading = ref(false)
const useRemote = ref(false)
const groupChatVisible = ref(false)
const labelDialogVisible = ref(false)
const labelTargetId = ref<string>()
const labelDraft = ref<string[]>([])

const messageApi = computed(() =>
  props.http ? createMessageApi(props.http, props.apiPrefix) : null
)

const list = useMessageList()
const {
  keyword,
  selectedId,
  summary,
  filteredConversations,
  selectedConversation,
  selectedThread,
} = list

watch(
  () => summary.value.unread,
  (count) => {
    setMessageCenterUnread(count)
    emit('unread-change', count)
  },
  { immediate: true }
)

function shouldUseRemote() {
  if (!messageApi.value) return false
  if (props.authenticated === false) return false
  return true
}

function applyMock() {
  useRemote.value = false
  list.clearRemoteSummary()
  const inbox = recordsToInbox(mockMessages)
  list.replaceInbox(
    [...mockChatConversations.map((item) => ({ ...item })), ...inbox.conversations],
    { ...mockChatThreads, ...inbox.threads }
  )
  selectInitialConversation()
}

function selectInitialConversation() {
  const id = props.conversationId
  if (!id) return
  if (!list.conversations.value.some((item) => item.id === id)) return
  if (list.selectedId.value === id) return
  void handleSelect(id)
}

async function loadRemote() {
  if (!messageApi.value) {
    if (props.fallbackToMock) applyMock()
    return
  }
  loading.value = true
  try {
    const [dtos, summaryData] = await Promise.all([
      messageApi.value.fetchConversations(),
      messageApi.value.fetchSummary(),
    ])
    const conversations = dtos.map(mapConversationDto)
    const threads: Record<string, ReturnType<typeof mapMessageDto>[]> = {}
    await Promise.all(
      conversations.map(async (item) => {
        try {
          const messages = await messageApi.value!.fetchMessages(item.id)
          threads[item.id] = messages.map(mapMessageDto)
        } catch {
          threads[item.id] = []
        }
      })
    )
    list.replaceInbox(conversations, threads)
    list.setRemoteSummary(summaryData)
    useRemote.value = true
    selectInitialConversation()
    emit('load', conversations)
  } catch (error) {
    emit('error', error as Error)
    if (props.fallbackToMock) {
      applyMock()
      ZMessage.warning(
        (error as Error)?.message ?? '消息接口请求失败，已使用本地 mock'
      )
    } else {
      useRemote.value = false
      list.clearRemoteSummary()
      list.replaceInbox([], {})
      ZMessage.error((error as Error)?.message ?? '消息接口请求失败')
    }
  } finally {
    loading.value = false
  }
}

async function reload() {
  if (shouldUseRemote()) {
    await loadRemote()
    return
  }
  applyMock()
}

function findConversation(id: string) {
  return list.conversations.value.find((item) => item.id === id)
}

async function handleSelect(id: string) {
  list.selectConversation(id)
  const conversation = list.selectedConversation.value
  if (conversation && conversation.unread > 0) {
    await handleMarkRead(id, false)
  }
}

async function handleMarkRead(id: string, showToast = true) {
  try {
    if (useRemote.value && messageApi.value) {
      await messageApi.value.markConversationRead(id)
    }
    list.markRead(id)
    if (showToast) ZMessage.success('已标为已读')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleToggleStar(id: string) {
  const conversation = findConversation(id)
  const next = !conversation?.starred
  try {
    if (useRemote.value && messageApi.value) {
      await messageApi.value.starConversation(id, next)
    }
    list.toggleStar(id, next)
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleMarkUnread(id: string) {
  const conversation = findConversation(id)
  if (!conversation || conversation.unread > 0) return
  try {
    if (useRemote.value && messageApi.value) {
      await messageApi.value.markConversationUnread(id)
    }
    list.markUnread(id)
    ZMessage.success('已标为未读')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleToggleMute(id: string) {
  const conversation = findConversation(id)
  const next = !conversation?.muted
  try {
    if (useRemote.value && messageApi.value) {
      await messageApi.value.muteConversation(id, next)
    }
    list.toggleMute(id, next)
    ZMessage.success(next ? '已开启免打扰' : '已取消免打扰')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleTogglePin(id: string) {
  const conversation = findConversation(id)
  const next = !conversation?.pinned
  try {
    if (useRemote.value && messageApi.value) {
      await messageApi.value.pinConversation(id, next)
    }
    list.togglePin(id, next)
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

function openLabelDialog(id: string) {
  const conversation = findConversation(id)
  labelTargetId.value = id
  labelDraft.value = [...(conversation?.labels ?? [])]
  labelDialogVisible.value = true
}

async function handleSaveLabels(labels: string[]) {
  const id = labelTargetId.value
  if (!id) return
  try {
    if (useRemote.value && messageApi.value) {
      await messageApi.value.updateConversationLabels(id, labels)
    }
    list.setLabels(id, labels)
    ZMessage.success('标签已更新')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleHide(id: string) {
  try {
    await ZMessageBox.confirm('确定不显示该会话？新消息到来时会重新出现。', '不显示', {
      type: 'warning',
      confirmButtonText: '不显示',
      cancelButtonText: '取消',
    })
  } catch {
    return
  }
  try {
    if (useRemote.value && messageApi.value) {
      await messageApi.value.hideConversation(id)
    }
    list.removeConversation(id)
    ZMessage.success('已从列表隐藏')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleClear(id: string) {
  try {
    await ZMessageBox.confirm('确定清空聊天记录？仅对自己生效，会话仍会保留。', '清空聊天记录', {
      type: 'warning',
      confirmButtonText: '清空',
      cancelButtonText: '取消',
    })
  } catch {
    return
  }
  try {
    if (useRemote.value && messageApi.value) {
      await messageApi.value.clearConversationMessages(id)
    }
    list.clearThread(id)
    ZMessage.success('聊天记录已清空')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleRemove(id: string) {
  try {
    await ZMessageBox.confirm('确定删除该会话？将从列表移除并清空你的聊天记录。', '删除会话', {
      type: 'warning',
      confirmButtonText: '删除',
      cancelButtonText: '取消',
    })
  } catch {
    return
  }
  try {
    if (useRemote.value && messageApi.value) {
      await messageApi.value.deleteConversation(id)
    }
    list.removeConversation(id)
    ZMessage.warning('会话已删除')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleMenuCommand(command: ConversationMenuCommand, id: string) {
  if (command === 'unread') {
    await handleMarkUnread(id)
    return
  }
  if (command === 'mute') {
    await handleToggleMute(id)
    return
  }
  if (command === 'pin') {
    await handleTogglePin(id)
    return
  }
  if (command === 'star') {
    await handleToggleStar(id)
    return
  }
  if (command === 'label') {
    openLabelDialog(id)
    return
  }
  if (command === 'hide') {
    await handleHide(id)
    return
  }
  if (command === 'clear') {
    await handleClear(id)
    return
  }
  if (command === 'delete') {
    await handleRemove(id)
  }
}

function handleOpen(conversation: Conversation) {
  if (!conversation.bizTab) return
  if (conversation.unread > 0) {
    void handleMarkRead(conversation.id, false)
  }
  emit('open-business', {
    tab: conversation.bizTab,
    message: conversationToRecord(conversation),
  })
  const label =
    props.bizTabLabels[conversation.bizTab]
    ?? BIZ_TAB_LABELS[conversation.bizTab]
    ?? conversation.bizTab
  ZMessage.info(`正在打开「${label}」相关业务`)
}

function parseUserId(raw: string) {
  const value = raw.replace(/^user:/, '')
  const num = Number(value)
  return Number.isFinite(num) ? num : undefined
}

async function handleCreateGroup(members: GroupChatMember[]) {
  const userIds = members
    .map((item) => parseUserId(item.id))
    .filter((id): id is number => typeof id === 'number')
  try {
    if (useRemote.value && messageApi.value && userIds.length) {
      const dto = await messageApi.value.createConversation({
        kind: userIds.length > 1 ? 'group' : 'direct',
        userIds,
      })
      if (dto) {
        const conversation = mapConversationDto(dto)
        const messages = await messageApi.value.fetchMessages(conversation.id)
        list.upsertConversation(conversation, messages.map(mapMessageDto))
        return
      }
    }
    const created = buildConversationFromMembers(members)
    list.upsertConversation(created.conversation, created.messages)
    ZMessage.success(
      created.conversation.kind === 'direct'
        ? `已发起与「${created.conversation.title}」的会话`
        : `已发起群聊「${created.conversation.title}」`
    )
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '创建会话失败')
  }
}

function handleDirectChat(member: ConversationMember) {
  void handleCreateGroup([
    {
      id: member.id,
      name: member.name,
      kind: member.kind ?? 'user',
      username: member.username,
      email: member.email,
    },
  ])
}

function currentChatId() {
  const conversation = list.selectedConversation.value
  if (!conversation || conversation.kind === 'notice' || conversation.kind === 'bot') {
    return undefined
  }
  return conversation.id
}

async function handleSendText(text: string) {
  const conversationId = currentChatId()
  if (!conversationId) return
  emit('send', { conversationId, text })
  try {
    if (useRemote.value && messageApi.value) {
      const dto = await messageApi.value.sendMessage(conversationId, {
        contentType: 'text',
        content: text,
      })
      if (dto) {
        list.appendMessage(mapMessageDto(dto))
        return
      }
    }
    list.appendMessage({
      id: nextMessageId(),
      conversationId,
      from: 'self',
      kind: 'text',
      content: text,
      createTime: new Date().toISOString(),
      read: true,
    })
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '发送失败')
  }
}

async function handleSendImage(file: File) {
  await sendAttachment(file, 'image')
}

async function handleSendFile(file: File) {
  await sendAttachment(file, 'file')
}

async function sendAttachment(file: File, contentType: 'image' | 'file') {
  const conversationId = currentChatId()
  if (!conversationId) return
  try {
    if (useRemote.value && messageApi.value) {
      if (!props.uploadFile) {
        ZMessage.error('文件上传未配置')
        return
      }
      const uploaded = await props.uploadFile(file)
      const dto = await messageApi.value.sendMessage(conversationId, {
        contentType,
        content: uploaded.url,
        fileName: uploaded.name || file.name,
      })
      if (dto) {
        list.appendMessage(mapMessageDto(dto))
        return
      }
    }
    list.appendMessage({
      id: nextMessageId(),
      conversationId,
      from: 'self',
      kind: contentType,
      content: URL.createObjectURL(file),
      fileName: file.name,
      createTime: new Date().toISOString(),
      read: true,
    })
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '发送失败')
  }
}

function applyRealtime(event: ImRealtimeEvent) {
  if (event.unreadTotal != null) {
    list.setRemoteSummary({
      ...summary.value,
      unread: event.unreadTotal,
    })
  }
  const eventId = event.conversationId ?? (event.conversation ? String(event.conversation.id) : undefined)
  if ((event.op === 'hide' || event.op === 'delete') && eventId) {
    list.removeConversation(eventId)
    return
  }
  if (event.op === 'clear' && eventId) {
    list.clearThread(eventId)
    return
  }
  if (event.message) {
    const message = mapMessageDto(event.message)
    if (!findConversation(message.conversationId) && event.conversation) {
      list.upsertConversation(mapConversationDto(event.conversation), [message])
      return
    }
    list.appendMessage(message)
    return
  }
  if (event.conversation) {
    const conversation = mapConversationDto(event.conversation)
    const current = findConversation(conversation.id)
    if (current) {
      Object.assign(current, conversation)
    } else {
      list.upsertConversation(conversation, [])
    }
  }
}

watch(
  () => props.conversationId,
  () => {
    selectInitialConversation()
  }
)

watch(
  () => props.realtimeEvent,
  (event) => {
    if (event) applyRealtime(event)
  }
)

onMounted(async () => {
  if (!props.autoLoad) {
    applyMock()
    return
  }
  if (shouldUseRemote()) {
    await loadRemote()
    return
  }
  applyMock()
})

defineExpose({
  reload,
  applyRealtime,
})
</script>
